// Emoji picker popover.
//
// `emoji-mart` + `@emoji-mart/data` do the heavy lifting — the
// searchable grid, recently-used section, skin-tone selector, and
// keyboard map all ship in the package. Our job is:
//
//   - Anchor the popover to the trigger, flipping if it runs off-screen.
//   - Wire the brief's visual spec through emoji-mart's theming surface
//     (width, height, cell size, surface colour, accent).
//   - Handle Escape + outside-click to close.
//
// The picker never imports a custom emoji dataset. The brief explicitly
// rules those out; we ship the Unicode default so weight stays small
// and licensing stays boring.

import { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";

import data from "@emoji-mart/data";
import { Picker as EmojiMartPicker } from "emoji-mart";

import { usePortalRoot } from "~/components/EmbedPortalContext";
import { usePortalPlacement } from "~/lib/portal-positioning";

/** Vertical gap between the picker and its anchor, in pixels. */
const ANCHOR_OFFSET = 8;
/** Width token mirror. The brief pins the popover at 320×400; we read
 *  the numeric value here so the positioning math doesn't need to
 *  measure the DOM on first render. */
const PICKER_WIDTH = 320;
const PICKER_HEIGHT = 400;

interface EmojiPickerProps {
  /** The trigger button. The picker positions itself 8px above or
   *  below it based on viewport room. */
  anchor: HTMLElement | null;
  /** Fires when the user picks an emoji. Parent closes the picker. */
  onSelect: (emoji: string) => void;
  /** Fires on Escape or outside click. */
  onClose: () => void;
}

type PickedEmoji = { native?: string };

export function EmojiPicker({ anchor, onSelect, onClose }: EmojiPickerProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const pickerHostRef = useRef<HTMLDivElement | null>(null);
  const onSelectRef = useRef(onSelect);
  const portalRoot = usePortalRoot();

  // emoji-mart's `theme` prop takes `"light" | "dark" | "auto"`. `auto`
  // reads `prefers-color-scheme` only — which diverges from us when
  // the app forces a theme via `data-theme`. Read that attribute so
  // the picker follows Nurion's theme, not the OS's.
  const theme = useMemo<"light" | "dark">(() => {
    if (typeof document === "undefined") return "light";
    const attr = document.documentElement.getAttribute("data-theme");
    if (attr === "dark" || attr === "light") return attr;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }, []);

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  // Compute the anchor's surface position from known picker
  // dimensions — no measuring the picker itself, which avoided a
  // first-frame flash at a smaller size before emoji-mart's lazy
  // content paints. Re-runs on scroll / resize so the popover tracks
  // its anchor when the timeline scrolls behind it. Surface comes
  // from `getPortalViewportSize` so an embed-mounted picker stays
  // inside the embed's bounding box.
  const placement = usePortalPlacement({
    anchor,
    portalRoot,
    computeViewportCoords: (anchorRect, surface) => {
      const surfaceTop = surface.offsetTop + 8;
      const surfaceBottom = surface.offsetTop + surface.height;
      const surfaceRight = surface.offsetLeft + surface.width;
      const spaceBelow = surfaceBottom - anchorRect.bottom;
      const flipsUp = spaceBelow < PICKER_HEIGHT + ANCHOR_OFFSET;
      const desiredTop = flipsUp
        ? anchorRect.top - PICKER_HEIGHT - ANCHOR_OFFSET
        : anchorRect.bottom + ANCHOR_OFFSET;
      const viewportTop = Math.max(
        surfaceTop,
        Math.min(desiredTop, surfaceBottom - PICKER_HEIGHT - 8),
      );
      const rawLeft = anchorRect.left;
      const maxLeft = surfaceRight - PICKER_WIDTH - 8;
      const viewportLeft = Math.max(surface.offsetLeft + 8, Math.min(rawLeft, maxLeft));
      return { viewportTop, viewportLeft };
    },
  });

  // Escape closes and restores focus to the anchor so keyboard users
  // don't land on `<body>`. Outside click closes quietly. We don't
  // trap focus inside the popover — emoji-mart handles arrow-key
  // navigation and Enter-to-select internally, and letting the user
  // Tab back to the document feels calmer than modal containment.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        anchor?.focus?.();
      }
    }
    function onPointerDown(e: MouseEvent) {
      const node = wrapperRef.current;
      if (!node) return;
      if (node.contains(e.target as Node)) return;
      if (anchor && anchor.contains(e.target as Node)) return;
      onClose();
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [anchor, onClose]);

  useEffect(() => {
    const host = pickerHostRef.current;
    if (!host) return;

    // On a touch screen the search field must not take focus on open: iOS
    // would raise the keyboard and zoom the page before anything is picked.
    // Cells are also larger there, so a fingertip lands on one emoji.
    const touch = window.matchMedia?.("(pointer: coarse)").matches ?? false;
    const picker = new EmojiMartPicker({
      data,
      onEmojiSelect: (emoji: PickedEmoji) => {
        if (emoji.native) onSelectRef.current(emoji.native);
      },
      autoFocus: !touch,
      previewPosition: "none",
      skinTonePosition: "bottom",
      perLine: touch ? 7 : 8,
      maxFrequentRows: 2,
      emojiButtonSize: touch ? 40 : 28,
      emojiSize: touch ? 26 : 18,
      navPosition: "top",
      searchPosition: "sticky",
      dynamicWidth: true,
      theme,
    });

    // emoji-mart's declaration does not expose its Picker as a DOM Node,
    // but the documented browser API returns an HTMLElement intended for appendChild.
    if (!(picker instanceof HTMLElement)) return;

    // iOS zooms the page when a focused field is under 16px.
    const fontFix = document.createElement("style");
    fontFix.textContent = "input[type=search] { font-size: 16px; }";
    picker.shadowRoot?.appendChild(fontFix);

    picker.style.width = "100%";
    picker.style.height = "100%";
    host.replaceChildren(picker);
    return () => {
      picker.remove();
    };
  }, [theme]);

  const style: React.CSSProperties = placement
    ? { position: placement.position, top: placement.top, left: placement.left, zIndex: 80 }
    : // Pre-position render — mount off-screen until the anchor's
      // position lands in the layout effect. `role`/aria are declared
      // directly on the inner popover container so AT users hear the
      // label regardless of render ordering. Standalone (no embed
      // root): `position: fixed` is fine; embed: still fine because
      // the off-screen coords don't hit the embed's clip box.
      { position: "fixed", top: -9999, left: -9999, zIndex: 80 };

  if (!portalRoot) return null;

  // `aria-label` names the popover; we deliberately don't claim
  // `role="dialog"` because we don't trap focus. Screen readers can
  // navigate the picker's internal controls directly.
  return createPortal(
    <div ref={wrapperRef} data-chat-emoji-picker="" aria-label="Pick an emoji" style={style}>
      <div
        style={{
          width: "var(--chat-emoji-picker-width)",
          height: "var(--chat-emoji-picker-height)",
          borderRadius: "var(--chat-radius-xl)",
          border: "1px solid var(--chat-surface-border)",
          background: "var(--chat-surface-overlay)",
          boxShadow: "var(--chat-shadow-popover)",
          overflow: "hidden",
        }}
      >
        {/* Read the app theme off `data-theme` (above). Trying to
        override emoji-mart's internal `--em-rgb-*` variables with our
        colour tokens doesn't work: those slots expect space-separated
        RGB triplets, not resolved colour strings. Passing the library's
        own `theme` option is the honest path. */}
        <div ref={pickerHostRef} style={{ width: "100%", height: "100%" }} />
      </div>
    </div>,
    portalRoot,
  );
}
